import React, { Component } from "react";
import Axios from "axios";
import Constant from "../Constant";
import "../../css/style.css";
import { getAccessToken } from "../../hooks/AccessToken";
import { toast } from "react-toastify";

function formatDateTime(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch (e) {
    return String(value);
  }
}

/** Question-mark icon; native tooltip on hover via `title`. */
function HelpTip({ text }) {
  return (
    <span
      className="text-info"
      title={text}
      role="img"
      aria-label={text}
      style={{
        cursor: "help",
        marginLeft: 6,
        display: "inline-flex",
        alignItems: "center",
        verticalAlign: "middle",
      }}
    >
      <i className="mdi mdi-help-circle-outline" aria-hidden="true" />
    </span>
  );
}

const SLIDER_TRACK_W = 77;
const SLIDER_KNOB = 16;
// Inset from track edge when knob is at left or right.
const SLIDER_PAD = 2;

/** Compact slide toggle: off (knob left) vs on (knob right). */
function PushScheduleSlider({
  enabled,
  disabled,
  busy,
  onToggle,
  ariaLabelledBy,
}) {
  const on = !!enabled;
  const knobLeft = on
    ? SLIDER_TRACK_W - SLIDER_KNOB - SLIDER_PAD
    : SLIDER_PAD;
  const trackH = SLIDER_KNOB + SLIDER_PAD * 2;

  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      aria-labelledby={ariaLabelledBy}
      aria-busy={!!busy}
      disabled={disabled}
      onClick={() => onToggle(!on)}
      style={{
        position: "relative",
        width: SLIDER_TRACK_W,
        height: trackH,
        borderRadius: trackH / 2,
        border: "none",
        padding: 0,
        cursor: disabled ? "not-allowed" : "pointer",
        backgroundColor: on ? "#198754" : "#dee2e6",
        transition: "background-color 0.2s ease",
        flexShrink: 0,
      }}
    >
      <span
        aria-hidden
        style={{
          position: "absolute",
          top: SLIDER_PAD,
          left: knobLeft,
          width: SLIDER_KNOB,
          height: SLIDER_KNOB,
          borderRadius: "50%",
          backgroundColor: "#fff",
          boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
          transition: "left 0.2s ease",
        }}
      />
    </button>
  );
}

const TOOLTIPS = {
  cooldownDays:
    "Minimum days before the same For You entry can be picked again for a scheduled push. Applied when you turn scheduled pushes on.",
  intervalHours:
    "How many hours between scheduled push attempts. This controls scheduler cadence.",
  pushSchedule:
    "Turn on to enable scheduled pushes. Turn off to stop scheduled sends; the worker still runs but skips sending.",
  lastTick:
    "Updated each time the For You scheduler runs. Use this as a heartbeat that the worker is alive.",
  nextAttempt:
    "Expected time of the next scheduler tick based on intervalHours. A push is only sent if enabled and tokens are available.",
  nextSelection:
    "One-shot override for the next send of any type. Manual selection bypasses cooldown; random-next respects cooldown based on next scheduled run time.",
  testUserId:
    "MongoDB user _id as stored in UserNotificationToken.userId—the same value the app registers when saving push tokens.",
  sendTestUser:
    "Send one random eligible For You Expo push only to this user's active device tokens. Updates lastPushNotifiedAt after a successful send.",
  sendNowAll:
    "Immediately send one random eligible For You broadcast to all active device tokens. Manual test run; does not wait for the scheduler. Updates lastPushNotifiedAt on success.",
  currentStatus:
    "Whether scheduled For You broadcasts are enabled. Mirrors the toggle; uses the cooldown above when enabled.",
  cooldownSummary:
    "Days before the same For You row can be chosen again after a successful push (eligibility for random selection).",
  removeRow:
    "Remove this story or poem from the curated For You list only. The underlying content is not deleted.",
  selectNextManual:
    "Use this post for the next push send. This bypasses cooldown once and clears after a successful send.",
};

class DataManagement extends Component {
  state = {
    DataList: [],
    length: 1,
    pageNo: 1,
    defaultPageNo: 1,
    loading: false,
    deletingIds: {},
    pushEnabled: false,
    cooldownDays: 7,
    intervalHours: 24,
    lastCronTickAt: null,
    nextScheduledRunAt: null,
    computedNextNotificationAt: null,
    nextSelection: null,
    statusLoading: false,
    startStopLoading: false,
    sendingToUser: false,
    sendingNow: false,
    selectingRandom: false,
    clearingNextSelection: false,
    selectingManualId: null,
    testUserId: "",
  };

  fetchPushStatus = async () => {
    try {
      this.setState({ statusLoading: true });
      const token = getAccessToken();
      const response = await Axios.get(
        `${Constant.apiBasePath}for-you-push/status`,
        { headers: { token } }
      );
      if (response.data && response.data.success) {
        const cfg = response.data.data || {};
        this.setState({
          pushEnabled: !!cfg.isEnabled,
          cooldownDays: Number(cfg.cooldownDays ?? 7),
          intervalHours: Number(cfg.intervalHours ?? 24),
          lastCronTickAt: cfg.lastCronTickAt || null,
          nextScheduledRunAt: cfg.nextScheduledRunAt || null,
          computedNextNotificationAt: cfg.computedNextNotificationAt || null,
          nextSelection: cfg.nextSelection || null,
        });
      }
    } catch (err) {
      console.log(err);
      toast.error("Could not fetch push status");
    } finally {
      this.setState({ statusLoading: false });
    }
  };

  startPush = async () => {
    try {
      this.setState({ startStopLoading: true });
      const token = getAccessToken();
      const response = await Axios.post(
        `${Constant.apiBasePath}for-you-push/start`,
        {
          cooldownDays: this.state.cooldownDays,
          intervalHours: this.state.intervalHours,
        },
        { headers: { token } }
      );

      if (response.data && response.data.success) {
        toast.success("For You push enabled");
        await this.fetchPushStatus();
      } else {
        toast.error(response.data?.message || "Failed to enable push");
      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to enable push");
    } finally {
      this.setState({ startStopLoading: false });
    }
  };

  stopPush = async () => {
    try {
      this.setState({ startStopLoading: true });
      const token = getAccessToken();
      const response = await Axios.post(
        `${Constant.apiBasePath}for-you-push/stop`,
        {},
        { headers: { token } }
      );

      if (response.data && response.data.success) {
        toast.success("For You push disabled");
        await this.fetchPushStatus();
      } else {
        toast.error(response.data?.message || "Failed to disable push");
      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to disable push");
    } finally {
      this.setState({ startStopLoading: false });
    }
  };

  sendTestToUser = async () => {
    const userId = String(this.state.testUserId || "").trim();
    if (!userId) {
      toast.warning("Please enter a userId for test send");
      return;
    }

    try {
      this.setState({ sendingToUser: true });
      const token = getAccessToken();
      const response = await Axios.post(
        `${Constant.apiBasePath}for-you-push/send-to-user`,
        { userId },
        { headers: { token } }
      );

      const result = response.data;
      if (result && result.success) {
        toast.success("Test notification sent");
        await this.fetchPushStatus();
      } else {
        toast.error(result?.message || "Failed to send test notification");
      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to send test notification");
    } finally {
      this.setState({ sendingToUser: false });
    }
  };

  sendNowToAll = async () => {
    try {
      this.setState({ sendingNow: true });
      const token = getAccessToken();
      const response = await Axios.post(
        `${Constant.apiBasePath}for-you-push/send-now`,
        {},
        { headers: { token } }
      );

      const result = response.data;
      if (result && result.success) {
        toast.success("Broadcast sent");
        await this.fetchPushStatus();
      } else {
        toast.error(result?.message || "Failed to send broadcast");
      }
    } catch (err) {
      console.log(err);
      toast.error("Failed to send broadcast");
    } finally {
      this.setState({ sendingNow: false });
    }
  };

  selectNextManual = async (forYouId) => {
    const id = String(forYouId || "");
    if (!id) return;
    try {
      this.setState({ selectingManualId: id });
      const token = getAccessToken();
      const response = await Axios.post(
        `${Constant.apiBasePath}for-you-push/select-next-manual`,
        { forYouId: id },
        { headers: { token } }
      );
      const result = response.data;
      if (result?.success) {
        toast.success("Next push post selected");
        await this.fetchPushStatus();
      } else {
        toast.error(result?.message || "Could not select next push post");
      }
    } catch (err) {
      console.log(err);
      toast.error("Could not select next push post");
    } finally {
      this.setState({ selectingManualId: null });
    }
  };

  selectNextRandom = async () => {
    try {
      this.setState({ selectingRandom: true });
      const token = getAccessToken();
      const response = await Axios.post(
        `${Constant.apiBasePath}for-you-push/select-next-random`,
        {},
        { headers: { token } }
      );
      const result = response.data;
      if (result?.success) {
        toast.success("Next push set to random (cooldown aware)");
        await this.fetchPushStatus();
      } else {
        toast.error(result?.message || "Could not set next random post");
      }
    } catch (err) {
      console.log(err);
      toast.error("Could not set next random post");
    } finally {
      this.setState({ selectingRandom: false });
    }
  };

  clearNextSelection = async () => {
    try {
      this.setState({ clearingNextSelection: true });
      const token = getAccessToken();
      const response = await Axios.post(
        `${Constant.apiBasePath}for-you-push/clear-next-selection`,
        {},
        { headers: { token } }
      );
      const result = response.data;
      if (result?.success) {
        toast.success("Next push selection cleared");
        await this.fetchPushStatus();
      } else {
        toast.error(result?.message || "Could not clear next push selection");
      }
    } catch (err) {
      console.log(err);
      toast.error("Could not clear next push selection");
    } finally {
      this.setState({ clearingNextSelection: false });
    }
  };

  handleSubmit = async (pageNo = 1) => {
    try {
      this.setState({ loading: true });
      const token = getAccessToken();
      const response = await Axios.post(
        Constant.apiBasePath + "for-you/list",
        { pageNo },
        { headers: { token } }
      );

      const { data, length, pageNo: currentPage } = response.data;
      this.setState({
        DataList: data || [],
        length: length || 1,
        pageNo: currentPage || 1,
        defaultPageNo: pageNo,
      });
    } catch (err) {
      console.log(err);
      toast.error("Could not load For You data");
    } finally {
      this.setState({ loading: false });
    }
  };

  deleteForYou = async (forYouId) => {
    const id = String(forYouId);
    this.setState((prev) => ({
      deletingIds: { ...prev.deletingIds, [id]: true },
    }));

    try {
      const token = getAccessToken();
      const response = await Axios.delete(Constant.apiBasePath + "for-you/" + id, {
        headers: { token },
      });

      if (response.data.status === Constant.statusSuccess) {
        toast.success("Removed from For You");
        this.handleSubmit(this.state.pageNo);
      } else {
        toast.error(response.data.message || "Could not remove entry");
      }
    } catch (err) {
      const msg =
        (err.response && err.response.data && err.response.data.message) ||
        err.message ||
        "Could not remove entry";
      toast.error(msg);
    } finally {
      this.setState((prev) => {
        const next = { ...prev.deletingIds };
        delete next[id];
        return { deletingIds: next };
      });
    }
  };

  componentDidMount() {
    this.handleSubmit(this.state.defaultPageNo);
    this.fetchPushStatus();
  }

  render() {
    const {
      DataList,
      length,
      pageNo,
      loading,
      deletingIds,
      pushEnabled,
      cooldownDays,
      intervalHours,
      lastCronTickAt,
      nextScheduledRunAt,
      computedNextNotificationAt,
      nextSelection,
      statusLoading,
      startStopLoading,
      sendingToUser,
      sendingNow,
      selectingRandom,
      clearingNextSelection,
      selectingManualId,
      testUserId,
    } = this.state;

    const displayNextAt =
      nextScheduledRunAt || computedNextNotificationAt || null;
    const nextSelectionMode = nextSelection?.mode || null;
    const nextSelectionForYou = nextSelection?.forYou || null;
    let i = (pageNo - 1) * Constant.perPage;

    const bodyData = DataList.map((el) => {
      i++;
      return (
        <tr key={el._id}>
          <td>{i}</td>
          <td>{el.contentType}</td>
          <td className="namecol">{el.title}</td>
          <td>{el.authorName || "-"}</td>
          <td className="namecol">{el.authorUserId || "-"}</td>
          <td className="namecol">{el.contentId}</td>
          <td>{el.lastPushNotifiedAt ? el.lastPushNotifiedAt : "-"}</td>
          <td>{el.createdAt}</td>
          <td className="actions-col for-you-actions-col">
            <div className="for-you-row-actions">
              <button
                type="button"
                className="btn btn-danger Banner-btn updatebtns"
                onClick={() => {
                  if (window.confirm("Remove this entry from For You?")) {
                    this.deleteForYou(el._id);
                  }
                }}
                disabled={!!deletingIds[String(el._id)]}
              >
                {deletingIds[String(el._id)] ? "Removing..." : "Remove"}
              </button>
              <button
                type="button"
                className="btn btn-primary Banner-btn updatebtns"
                onClick={() => this.selectNextManual(el._id)}
                disabled={selectingManualId === String(el._id)}
              >
                {selectingManualId === String(el._id)
                  ? "Selecting..."
                  : "Set Next Push"}
              </button>
              <HelpTip text={TOOLTIPS.removeRow} />
              <HelpTip text={TOOLTIPS.selectNextManual} />
            </div>
          </td>
        </tr>
      );
    });

    const previous = pageNo - 1;
    const next = pageNo + 1;
    const customLength = length || 1;

    return (
      <div className="content-page contest-submissions-page contest-management-page for-you-management-page event-consistent-page">
        <div className="content page-content-spaced">
          <div className="event-management-header">
            <div className="header-content">
              <div className="header-title">
                <h4 className="page-title-heading">For You Management</h4>
                <p className="header-subtitle">
                  Configure For You push behavior and manage curated entries.
                </p>
              </div>
            </div>
          </div>

          <div className="event-management-body">
            <div className="card contest-like-card">
              <div className="card-body">
                <div className="for-you-settings-header">
                  <h5 className="for-you-settings-title">For You Push Notifications</h5>
                </div>

                <div className="for-you-settings-grid">
                  <div className="for-you-field">
                    <label>
                      Cooldown Days
                      <HelpTip text={TOOLTIPS.cooldownDays} />
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={cooldownDays}
                      min={0}
                      disabled={startStopLoading || statusLoading}
                      onChange={(e) =>
                        this.setState({ cooldownDays: Number(e.target.value) })
                      }
                    />
                  </div>

                  <div className="for-you-field">
                    <label>
                      Interval Hours
                      <HelpTip text={TOOLTIPS.intervalHours} />
                    </label>
                    <input
                      type="number"
                      className="form-control"
                      value={intervalHours}
                      min={1}
                      disabled={startStopLoading || statusLoading}
                      onChange={(e) =>
                        this.setState({ intervalHours: Number(e.target.value) })
                      }
                    />
                  </div>

                  <div className="for-you-schedule-toggle">
                    <span id="forYouPushScheduleLabel" className="for-you-label-text">
                      Scheduled pushes
                    </span>
                    <PushScheduleSlider
                      enabled={pushEnabled}
                      disabled={startStopLoading || statusLoading}
                      busy={startStopLoading}
                      ariaLabelledBy="forYouPushScheduleLabel"
                      onToggle={(wantOn) =>
                        wantOn ? this.startPush() : this.stopPush()
                      }
                    />
                    <HelpTip text={TOOLTIPS.pushSchedule} />
                  </div>
                </div>

                <div className="for-you-status-lines">
                  <p>
                    <strong>Last scheduler tick:</strong> {formatDateTime(lastCronTickAt)}
                    <HelpTip text={TOOLTIPS.lastTick} />
                  </p>
                  <p>
                    <strong>Next notification attempt:</strong> {formatDateTime(displayNextAt)}
                    <HelpTip text={TOOLTIPS.nextAttempt} />
                  </p>
                  {!lastCronTickAt && pushEnabled && (
                    <p className="for-you-heartbeat-note">
                      The first heartbeat appears after the server has run one full
                      scheduler interval, or refresh after that.
                    </p>
                  )}
                </div>

                <div className="for-you-next-selection-card">
                  <p className="for-you-next-selection-title">
                    Next Push Post
                    <HelpTip text={TOOLTIPS.nextSelection} />
                  </p>
                  <p className="for-you-next-selection-text">
                    {nextSelectionMode === "manual_bypass" && nextSelectionForYou
                      ? `Manual (bypass cooldown): ${nextSelectionForYou.title} [${nextSelectionForYou.contentType}]`
                      : nextSelectionMode === "random_respecting_cooldown" && nextSelectionForYou
                      ? `Random (cooldown-respecting): ${nextSelectionForYou.title} [${nextSelectionForYou.contentType}]`
                      : "No override selected (fallback random mode)."}
                  </p>
                  {nextSelectionForYou && (
                    <p className="for-you-next-selection-meta">
                      contentId: {nextSelectionForYou.contentId}
                    </p>
                  )}
                  <div className="for-you-next-selection-actions">
                    <button
                      type="button"
                      className="btn btn-secondary"
                      onClick={this.selectNextRandom}
                      disabled={selectingRandom}
                    >
                      {selectingRandom ? "Selecting random..." : "Select random post"}
                    </button>
                    <button
                      type="button"
                      className="btn btn-light"
                      onClick={this.clearNextSelection}
                      disabled={clearingNextSelection}
                    >
                      {clearingNextSelection ? "Clearing..." : "Clear next selection"}
                    </button>
                  </div>
                </div>

                <div className="for-you-send-row">
                  <div className="for-you-send-controls">
                    <div className="for-you-field for-you-userid-field">
                      <label>
                        Test userId
                        <HelpTip text={TOOLTIPS.testUserId} />
                      </label>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Enter userId"
                        value={testUserId}
                        onChange={(e) => this.setState({ testUserId: e.target.value })}
                        disabled={sendingToUser}
                      />
                    </div>

                    <div className="for-you-inline-action">
                      <button
                        type="button"
                        className="btn btn-info"
                        onClick={this.sendTestToUser}
                        disabled={sendingToUser}
                      >
                        {sendingToUser ? "Sending..." : "Send test to user"}
                      </button>
                      <HelpTip text={TOOLTIPS.sendTestUser} />
                    </div>

                    <div className="for-you-inline-action">
                      <button
                        type="button"
                        className="btn btn-warning"
                        onClick={this.sendNowToAll}
                        disabled={sendingNow}
                      >
                        {sendingNow ? "Sending..." : "Send now to all"}
                      </button>
                      <HelpTip text={TOOLTIPS.sendNowAll} />
                    </div>
                  </div>

                  <div className="for-you-summary-card">
                    <p>
                      <strong>Current:</strong> {pushEnabled ? "Enabled" : "Disabled"}
                      <HelpTip text={TOOLTIPS.currentStatus} />
                    </p>
                    <p>
                      Cooldown: {Number(cooldownDays)} days
                      <HelpTip text={TOOLTIPS.cooldownSummary} />
                    </p>
                    <p>Interval: {Number(intervalHours)} hours</p>
                  </div>
                </div>

                <div className="table-wrapper contest-submissions-table-responsive contest-like-table-responsive for-you-table-wrap">
                  <table
                    id="complex-header-datatable"
                    className="table event-table contest-submissions-table contest-like-table for-you-management-table"
                  >
                    <thead>
                      <tr>
                        <th className="for-you-sno-col">S.No.</th>
                        <th className="for-you-type-col">Type</th>
                        <th className="for-you-title-col">Title</th>
                        <th>Author Name</th>
                        <th>Author ID</th>
                        <th className="for-you-contentid-col">Content ID</th>
                        <th>Last Push Used</th>
                        <th>Created At</th>
                        <th className="actions-col">Action</th>
                      </tr>
                    </thead>
                    <tbody>{bodyData}</tbody>
                  </table>
                </div>

                {!loading && (
                  <div className="pagination-wrapper">
                    <div className="pagination">
                      {pageNo <= 1 ? (
                        <a className="default-previous">Previous</a>
                      ) : (
                        <a
                          className="paginate-link"
                          onClick={() => this.handleSubmit(previous)}
                        >
                          Previous
                        </a>
                      )}
                      <a>{pageNo}</a>
                      {pageNo >= customLength ? (
                        <a className="default-next">Next</a>
                      ) : (
                        <a
                          className="paginate-link active"
                          onClick={() => this.handleSubmit(next)}
                        >
                          Next
                        </a>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }
}

export default DataManagement;
